import {
  CFormCheck,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DeleteIcon from "../../components/icons/DeleteIcon";
import EditIcon from "../../components/icons/EditIcon";
import PromptModal from "../../components/modals/PromptModal";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import { iconBtnStyle, tdStyle, thStyle } from "../../helpers/constants";
import { Chip } from "../../helpers/general";

/* ─────────────── Table shell ─────────────── */

export function TranslationTable({
  sourceLabel,
  translationLabel = "Traduction",
  children,
}: {
  sourceLabel: string;
  translationLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-3">
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell style={{ ...thStyle, width: 44 }} />
            <CTableHeaderCell scope="col" style={thStyle}>
              {sourceLabel}
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              {translationLabel}
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Langue
            </CTableHeaderCell>
            <CTableHeaderCell
              scope="col"
              style={{ ...thStyle, textAlign: "right" }}
            >
              Actions
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>{children}</CTableBody>
      </CTable>
    </div>
  );
}

/* ─────────────── Generic row ─────────────── */

interface TranslationRowProps {
  id: string;
  deletedList: React.Dispatch<BulkDeleteActions>;
  /** Name of the translated item (offer, service, place...) */
  sourceTitle?: string;
  sourceSubtitle?: string;
  sourceHref?: string;
  /** Show a thumbnail next to the source name */
  withImage?: boolean;
  image?: string | null;
  /** The translated text */
  translation?: string;
  lang?: string;
  editTo: string;
  onDelete: () => void;
  isDeleting: boolean;
  modalMessage?: string;
}

export function TranslationRow({
  id,
  deletedList,
  sourceTitle,
  sourceSubtitle,
  sourceHref,
  withImage = false,
  image,
  translation,
  lang,
  editTo,
  onDelete,
  isDeleting,
  modalMessage,
}: TranslationRowProps) {
  const [isChecked, setIsChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (isChecked) {
      deletedList({ type: DELETE_ACTIONS.ADD, id });
    } else {
      deletedList({ type: DELETE_ACTIONS.REMOVE, id });
    }
  }, [deletedList, isChecked, id]);

  return (
    <CTableRow
      style={{
        backgroundColor: isChecked ? "rgba(240,129,63,0.06)" : undefined,
        boxShadow: isChecked ? `inset 3px 0 0 ${colors.primary}` : undefined,
      }}
    >
      {/* Checkbox */}
      <CTableDataCell style={{ ...tdStyle, width: 44 }}>
        <CFormCheck
          checked={isChecked}
          onChange={(e) => setIsChecked(e.target.checked)}
        />
      </CTableDataCell>

      {/* Source item */}
      <CTableDataCell style={tdStyle}>
        <div
          className="d-flex align-items-center gap-2"
          style={{ minWidth: 200 }}
        >
          {withImage && (
            <img
              src={
                image ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  sourceTitle ?? "",
                )}`
              }
              alt=""
              width={40}
              height={40}
              className="flex-shrink-0"
              style={{ borderRadius: 10, objectFit: "cover" }}
            />
          )}
          <div style={{ minWidth: 0 }}>
            {sourceHref ? (
              <Link
                to={sourceHref}
                className="fw-semibold text-capitalize d-block text-truncate"
                style={{ maxWidth: 260 }}
              >
                {sourceTitle}
              </Link>
            ) : (
              <div
                className="fw-semibold text-capitalize text-truncate"
                style={{ maxWidth: 260 }}
              >
                {sourceTitle}
              </div>
            )}
            {sourceSubtitle && (
              <div className="text-muted" style={{ fontSize: 12 }}>
                {sourceSubtitle}
              </div>
            )}
          </div>
        </div>
      </CTableDataCell>

      {/* Translation */}
      <CTableDataCell style={tdStyle}>
        <span className="fw-semibold text-capitalize">{translation}</span>
      </CTableDataCell>

      {/* Language */}
      <CTableDataCell style={tdStyle}>
        {lang ? (
          <Chip>{lang.toUpperCase()}</Chip>
        ) : (
          <span className="text-muted">—</span>
        )}
      </CTableDataCell>

      {/* Actions */}
      <CTableDataCell style={tdStyle}>
        <div className="d-flex align-items-center justify-content-end gap-2">
          <Link
            to={editTo}
            title="Modifier"
            aria-label="Modifier"
            className="d-inline-flex align-items-center justify-content-center rounded-circle"
            style={iconBtnStyle}
          >
            <EditIcon width="16" height="16" fill={colors.secondary} />
          </Link>
          <button
            type="button"
            title="Supprimer"
            aria-label="Supprimer"
            onClick={() => setShowModal(true)}
            disabled={isDeleting}
            className="d-inline-flex align-items-center justify-content-center border-0 rounded-circle"
            style={{ ...iconBtnStyle, backgroundColor: "rgba(220,53,69,0.10)" }}
          >
            {isDeleting ? (
              <CSpinner size="sm" />
            ) : (
              <DeleteIcon width="18" height="18" fill={colors.primary} />
            )}
          </button>
        </div>
      </CTableDataCell>

      <PromptModal
        open={showModal}
        onAccept={onDelete}
        onClose={() => setShowModal(false)}
        {...(modalMessage ? { message: modalMessage } : {})}
        {...(modalMessage ? { loading: isDeleting } : {})}
      />
    </CTableRow>
  );
}
